import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import countryCodes from "../lib/countryCodes";
import { Car, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dialCode, setDialCode] = useState("+250");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Where to go after registration — back to checkout or account
  const from = location.state?.from || null;

  React.useEffect(() => {
    if (location.state?.formData) {
      setForm(location.state.formData);
      const parts = (location.state.formData.name || "").trim().split(/\s+/);
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" ") || "");
    }
    if (location.state?.agreed) {
      setAgreedToTerms(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim() || !lastName.trim()) {
      setError("Please enter both your first and last name.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    const fullForm = { ...form, name: `${firstName.trim()} ${lastName.trim()}`, phone: phoneNumber.trim() ? `${dialCode}${phoneNumber.trim()}` : "" };
    setLoading(true);
    const res = await register(fullForm);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created — welcome!");
      let redirectTo = from;
      if (!redirectTo) {
        try {
          if (localStorage.getItem("rx_intent") === "host") {
            redirectTo = "/owner/cars/new";
          }
        } catch {}
      }
      redirectTo = redirectTo || "/";
      try {
        if (redirectTo.startsWith("/owner")) {
          localStorage.setItem(`rx_mode_${res.user.id}`, "hosting");
        } else if (redirectTo.startsWith("/account")) {
          localStorage.setItem(`rx_mode_${res.user.id}`, "renting");
        }
        localStorage.removeItem("rx_intent");
      } catch {}
      nav(redirectTo, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-lg px-4 py-16">
        <Card className="border-border shadow-organic p-8 fade-up">
          <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-4">
            <Car className="text-primary-foreground" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Join RydeXpress — rent or list your car, anytime.</p>

          {error && <p className="mt-4 text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-2">{error}</p>}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">First name</Label>
                <Input className="mt-1.5" required placeholder="Joe"
                  value={firstName} onChange={e => setFirstName(e.target.value)}
                  data-testid="register-first-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Last name</Label>
                <Input className="mt-1.5" required placeholder="Doe"
                  value={lastName} onChange={e => setLastName(e.target.value)}
                  data-testid="register-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Phone</Label>
              <div className="mt-1.5 flex gap-2">
                <select
                  value={dialCode}
                  onChange={e => setDialCode(e.target.value)}
                  className="h-11 rounded-md border border-input bg-transparent px-2 text-sm shrink-0 w-[110px]"
                  data-testid="register-phone-country"
                >
                  {countryCodes.map(c => (
                    <option key={c.code} value={c.dial}>{c.code} {c.dial}</option>
                  ))}
                </select>
                <Input placeholder="788 000 000"
                  value={phoneNumber} onChange={e => setPhoneNumber(e.target.value.replace(/[^0-9\s]/g, ""))}
                  inputMode="numeric"
                  data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
              <Input className="mt-1.5" type="email" required placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="register-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? "text" : "password"} required minLength={6} placeholder="At least 6 characters" className="pr-10"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="register-password" />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                id="agree-terms"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 accent-primary"
                data-testid="register-agree-terms"
              />
              <label htmlFor="agree-terms" className="text-sm text-muted-foreground">
                I agree to RydeXpress&rsquo;s{" "}
                <Link to="/legal/terms" state={{ formData: { ...form, name: `${firstName.trim()} ${lastName.trim()}` } }} className="text-primary hover:underline">Terms &amp; Conditions and Privacy Policy</Link>.
              </label>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90 btn-lift mt-2"
              data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create account"}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account? <Link to="/login" state={{ from }} className="text-primary font-medium hover:underline" data-testid="goto-login">Sign in</Link>
            </p>
          </form>
        </Card>
      </section>
    </PublicLayout>
  );
}
